import { useEffect, useState } from "react";
import * as Icons from "lucide-react";
import { toast } from "sonner";
import { api, apiError } from "../../lib/api";

const btnPrimary =
  "rounded-md bg-elv-green px-3.5 py-1.5 text-[11px] font-semibold text-[#04120A] disabled:opacity-60";
const btnGhost =
  "rounded-md border border-elv-line px-3.5 py-1.5 text-[11px] text-elv-dim hover:text-elv-text disabled:opacity-60";
const inputCls =
  "w-full rounded-md border border-elv-line bg-white/[0.03] px-3 py-2 text-xs text-elv-text placeholder:text-elv-dim/60 focus:border-elv-green/50 focus:outline-none";

export const VapiPanel = ({ onStatus }) => {
  const [cfg, setCfg] = useState(null);
  const [open, setOpen] = useState(false);
  const [apiKey, setApiKey] = useState("");
  const [assistantId, setAssistantId] = useState("");
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get("/integrations/vapi/config");
      setCfg(data);
      setAssistantId(data.assistant_id || "");
    } catch (e) {
      /* card still renders */
    }
  };
  useEffect(() => { load(); }, []);

  const save = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.put("/integrations/vapi/config", {
        api_key: apiKey, assistant_id: assistantId || null,
      });
      toast[data.status === "connected" ? "success" : "warning"](data.message);
      setApiKey("");
      onStatus?.(data.status);
      await load();
    } catch (e2) {
      toast.error(apiError(e2));
    } finally {
      setBusy(false);
    }
  };

  const remove = async () => {
    setBusy(true);
    try {
      await api.delete("/integrations/vapi/config");
      toast.success("Vapi credentials removed.");
      onStatus?.("not_connected");
      await load();
    } catch (e2) {
      toast.error(apiError(e2));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div data-testid="vapi-panel" className="mt-4 border-t border-elv-line pt-4">
      <p className="text-[11px] text-elv-dim">
        {cfg?.has_key
          ? cfg.message || "Credentials saved."
          : "The live voice agent is fully wired: webhook, call logging and appointment booking are ready. Add your Vapi private API key to go live."}
      </p>

      {cfg?.webhook_url ? (
        <div className="mt-3">
          <p className="text-[10px] uppercase tracking-[0.1em] text-elv-dim">Your webhook URL (set as Server URL in Vapi)</p>
          <div className="mt-1 flex items-center gap-2">
            <code data-testid="vapi-webhook-url" className="flex-1 truncate rounded-md border border-elv-line bg-white/[0.03] px-2 py-1.5 font-mono text-[10px] text-elv-cyan">
              {cfg.webhook_url}
            </code>
            <button
              type="button"
              data-testid="vapi-copy-webhook"
              className={btnGhost}
              onClick={() => { navigator.clipboard.writeText(cfg.webhook_url); toast.success("Webhook URL copied."); }}
            >
              <Icons.Copy className="h-3 w-3" />
            </button>
          </div>
        </div>
      ) : null}

      <div className="mt-3 flex flex-wrap gap-2">
        <button type="button" data-testid="vapi-configure-btn" className={btnPrimary} onClick={() => setOpen(!open)}>
          {cfg?.has_key ? "Update credentials" : "Configure Vapi"}
        </button>
        {cfg?.has_key ? (
          <button type="button" data-testid="vapi-remove-btn" className={btnGhost} disabled={busy} onClick={remove}>
            Remove credentials
          </button>
        ) : null}
      </div>

      {open ? (
        <form onSubmit={save} data-testid="vapi-config-form" className="mt-3 grid gap-2">
          <input
            type="password"
            required
            autoComplete="off"
            data-testid="vapi-api-key-input"
            placeholder="Vapi private API key (from dashboard.vapi.ai)"
            value={apiKey}
            onChange={(e) => setApiKey(e.target.value)}
            className={inputCls}
          />
          <input
            type="text"
            data-testid="vapi-assistant-id-input"
            placeholder="Assistant ID (optional)"
            value={assistantId}
            onChange={(e) => setAssistantId(e.target.value)}
            className={inputCls}
          />
          <div className="flex gap-2">
            <button type="submit" data-testid="vapi-save-btn" disabled={busy || apiKey.length < 20} className={btnPrimary}>
              {busy ? "Verifying with Vapi…" : "Save & verify"}
            </button>
            <button type="button" className={btnGhost} onClick={() => setOpen(false)}>Cancel</button>
          </div>
          <p className="text-[10px] text-elv-dim">
            The key is verified against the Vapi API before anything is marked connected, then encrypted at rest.
          </p>
        </form>
      ) : null}
    </div>
  );
};

export const CalendarPanel = ({ onStatus }) => {
  const [cfg, setCfg] = useState(null);
  const [busy, setBusy] = useState(null);

  const load = async () => {
    try {
      const { data } = await api.get("/integrations/google-calendar/config");
      setCfg(data);
    } catch (e) { /* noop */ }
  };
  useEffect(() => { load(); }, []);

  const connect = async () => {
    setBusy("connect");
    try {
      const { data } = await api.get("/integrations/google-calendar/auth-url");
      window.location.href = data.authorization_url;
    } catch (e) {
      toast.warning(apiError(e));
      setBusy(null);
    }
  };

  const sync = async () => {
    setBusy("sync");
    try {
      const { data } = await api.post("/integrations/google-calendar/sync");
      toast.success(`Sync complete — pushed ${data.pushed}, pulled ${data.pulled}.`);
      await load();
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setBusy(null);
    }
  };

  const disconnect = async () => {
    setBusy("disconnect");
    try {
      await api.post("/integrations/google-calendar/disconnect-oauth");
      toast.success("Google Calendar disconnected.");
      onStatus?.("not_connected");
      await load();
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setBusy(null);
    }
  };

  return (
    <div data-testid="calendar-panel" className="mt-4 border-t border-elv-line pt-4">
      {cfg && !cfg.oauth_client_configured ? (
        <p data-testid="calendar-needs-config" className="text-[11px] text-elv-dim">
          The two-way sync and OAuth connect flow are built and waiting. {cfg.message}
        </p>
      ) : null}
      {cfg?.connected ? (
        <p data-testid="calendar-connected-info" className="text-[11px] text-elv-dim">
          Connected as <span className="text-elv-green">{cfg.account_email}</span>
          {cfg.last_sync ? ` · last sync ${new Date(cfg.last_sync).toLocaleString()}` : " · not synced yet"}
        </p>
      ) : null}

      <div className="mt-3 flex flex-wrap gap-2">
        {!cfg?.connected ? (
          <button type="button" data-testid="calendar-connect-btn" className={btnPrimary} disabled={busy === "connect"} onClick={connect}>
            Connect Google Calendar
          </button>
        ) : (
          <>
            <button type="button" data-testid="calendar-sync-btn" className={btnPrimary} disabled={busy === "sync"} onClick={sync}>
              {busy === "sync" ? "Syncing…" : "Sync now"}
            </button>
            <button type="button" data-testid="calendar-disconnect-btn" className={btnGhost} disabled={busy === "disconnect"} onClick={disconnect}>
              Disconnect
            </button>
          </>
        )}
      </div>
    </div>
  );
};


export const TwilioPanel = ({ onStatus }) => {
  const [cfg, setCfg] = useState(null);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ account_sid: "", auth_token: "", from_number: "", whatsapp_from: "" });
  const [busy, setBusy] = useState(false);
  const [testTo, setTestTo] = useState("");

  const load = async () => {
    try {
      const { data } = await api.get("/integrations/twilio/config");
      setCfg(data);
    } catch { /* card still renders */ }
  };
  useEffect(() => { load(); }, []);

  const save = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.put("/integrations/twilio/config", form);
      toast[data.status === "connected" ? "success" : "warning"](data.message);
      setForm({ account_sid: "", auth_token: "", from_number: "", whatsapp_from: "" });
      onStatus?.(data.status);
      await load();
      setOpen(false);
    } catch (e2) { toast.error(apiError(e2)); }
    finally { setBusy(false); }
  };

  const remove = async () => {
    setBusy(true);
    try {
      await api.delete("/integrations/twilio/config");
      toast.success("Twilio credentials removed.");
      onStatus?.("not_connected");
      await load();
    } catch (e2) { toast.error(apiError(e2)); }
    finally { setBusy(false); }
  };

  const sendTest = async () => {
    if (!testTo) return;
    setBusy(true);
    try {
      const { data } = await api.post("/integrations/twilio/send-test", {
        to: testTo, channel: "sms", body: "Elvoria AI test message — you can safely ignore this.",
      });
      toast.success(`SMS queued (${data.status}).`);
    } catch (e2) { toast.error(apiError(e2)); }
    finally { setBusy(false); }
  };

  return (
    <div data-testid="twilio-panel" className="mt-4 border-t border-elv-line pt-4">
      <p className="text-[11px] text-elv-dim">
        {cfg?.has_credentials
          ? cfg.message || `Twilio ready. From ${cfg.from_number}${cfg.whatsapp_enabled ? " · WhatsApp enabled" : ""}.`
          : "Twilio powers real SMS and WhatsApp sending from your automation engine. Paste your Twilio credentials to activate."}
      </p>

      <div className="mt-3 flex flex-wrap gap-2">
        <button type="button" data-testid="twilio-configure-btn" className={btnPrimary} onClick={() => setOpen(!open)}>
          {cfg?.has_credentials ? "Update credentials" : "Configure Twilio"}
        </button>
        {cfg?.has_credentials ? (
          <button type="button" data-testid="twilio-remove-btn" className={btnGhost} disabled={busy} onClick={remove}>
            Remove credentials
          </button>
        ) : null}
      </div>

      {open ? (
        <form onSubmit={save} data-testid="twilio-config-form" className="mt-3 grid gap-2">
          <input required autoComplete="off" data-testid="twilio-sid-input" placeholder="Account SID (AC…)" value={form.account_sid} onChange={(e) => setForm({ ...form, account_sid: e.target.value })} className={inputCls} />
          <input required type="password" autoComplete="off" data-testid="twilio-token-input" placeholder="Auth Token" value={form.auth_token} onChange={(e) => setForm({ ...form, auth_token: e.target.value })} className={inputCls} />
          <input required data-testid="twilio-from-input" placeholder="SMS number in E.164 (e.g. +14155551234)" value={form.from_number} onChange={(e) => setForm({ ...form, from_number: e.target.value })} className={inputCls} />
          <input data-testid="twilio-whatsapp-input" placeholder="WhatsApp sender in E.164 (optional)" value={form.whatsapp_from} onChange={(e) => setForm({ ...form, whatsapp_from: e.target.value })} className={inputCls} />
          <div className="flex gap-2">
            <button type="submit" data-testid="twilio-save-btn" disabled={busy} className={btnPrimary}>
              {busy ? "Verifying with Twilio…" : "Save & verify"}
            </button>
            <button type="button" className={btnGhost} onClick={() => setOpen(false)}>Cancel</button>
          </div>
          <p className="text-[10px] text-elv-dim">
            Credentials are verified against Twilio before anything is marked connected, then encrypted at rest.
          </p>
        </form>
      ) : null}

      {cfg?.has_credentials && cfg?.status === "connected" ? (
        <div className="mt-3 flex flex-wrap items-center gap-2">
          <input data-testid="twilio-test-to" placeholder="Test recipient (E.164)" value={testTo} onChange={(e) => setTestTo(e.target.value)} className={inputCls + " max-w-xs"} />
          <button type="button" data-testid="twilio-test-btn" className={btnGhost} disabled={busy || !testTo} onClick={sendTest}>
            Send test SMS
          </button>
        </div>
      ) : null}
    </div>
  );
};

export const StripePanel = () => {
  const [cfg, setCfg] = useState(null);
  useEffect(() => { api.get("/payments/config").then(({ data }) => setCfg(data)).catch(() => {}); }, []);
  return (
    <div data-testid="stripe-panel" className="mt-4 border-t border-elv-line pt-4">
      <p className="text-[11px] text-elv-dim">
        {cfg?.connected
          ? `Stripe checkout is active (mode: ${cfg.mode}). Use test card 4242 4242 4242 4242 in test mode.`
          : "Stripe is not configured. Add the STRIPE_API_KEY environment variable to enable checkout."}
      </p>
      <div className="mt-3 flex flex-wrap gap-2">
        <a href="/app/payments" data-testid="stripe-open-payments" className={btnPrimary}>
          Open payments
        </a>
      </div>
    </div>
  );
};

export const ResendPanel = () => {
  const [cfg, setCfg] = useState(null);
  const [to, setTo] = useState("");
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    api.get("/integrations/email/config").then(({ data }) => setCfg(data)).catch(() => {});
  }, []);

  const sendTest = async () => {
    if (!to) return;
    setBusy(true);
    try {
      const { data } = await api.post("/integrations/email/send-test", { to });
      toast.success(`Email sent to ${data.to} (id ${data.email_id?.slice(0, 8) || "queued"}).`);
      setTo("");
    } catch (e) { toast.error(apiError(e)); }
    finally { setBusy(false); }
  };

  const connected = cfg?.status === "connected";
  return (
    <div data-testid="resend-panel" className="mt-4 border-t border-elv-line pt-4">
      <p className="text-[11px] text-elv-dim">
        {connected
          ? `Transactional email is live via Emergent's managed Resend integration. Sends appear as "${cfg.from_name}".`
          : "Resend is not configured — automation emails will be logged as skipped until it is."}
      </p>
      {connected ? (
        <div className="mt-3 flex flex-wrap items-center gap-2">
          <input
            data-testid="resend-test-to"
            type="email"
            placeholder="Recipient email"
            value={to}
            onChange={(e) => setTo(e.target.value)}
            className={inputCls + " max-w-xs"}
          />
          <button type="button" data-testid="resend-test-btn"
                  className={btnPrimary} disabled={busy || !to} onClick={sendTest}>
            {busy ? "Sending…" : "Send test email"}
          </button>
        </div>
      ) : null}
      <p className="mt-2 text-[10px] text-elv-dim">
        Elvoria AI uses this to send password-reset emails, automation touchpoints, and the weekly report digest to org owners.
      </p>
    </div>
  );
};

